// Reusable jury system — a person exists once in a central library and can be
// associated with several editions, with a per-edition role/title.
//
// Data model:
//   festivalArchive.juryLibrary = [{ id, name, photo, bio, role }]   (unique fiches)
//   edition.jury = [{ personId, role, hidden }]                       (associations)
// Legacy editions may still hold inline members ({ id, name, role, photo, bio }).
// resolveJuryMembers() supports BOTH shapes so nothing ever breaks.

export const personId = () => `jp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Turn an edition's jury (refs and/or legacy inline) into full member objects.
export const resolveJuryMembers = (edition, library = []) => {
  const byId = {};
  (library || []).forEach((p) => { if (p && p.id) byId[p.id] = p; });
  return (edition?.jury || [])
    .map((item) => {
      if (!item) return null;
      if (item.personId) {
        const p = byId[item.personId];
        if (!p) return null; // fiche removed from library → drop the association
        return { ...p, id: p.id, role: (item.role ?? p.role) || "", hidden: item.hidden };
      }
      return item; // legacy inline member
    })
    .filter(Boolean);
};

// One-time, non-destructive migration: move inline members into the central
// library and replace edition entries with { personId, role } references.
// Returns { archive, changed }. Safe to run repeatedly (idempotent).
export const migrateJuryData = (archive) => {
  if (!archive || typeof archive !== "object") return { archive, changed: false };
  const library = Array.isArray(archive.juryLibrary) ? [...archive.juryLibrary] : [];
  const byId = {};
  const byName = {};
  library.forEach((p) => { if (p?.id) byId[p.id] = p; if (p?.name) byName[p.name.trim().toLowerCase()] = p; });

  let changed = !Array.isArray(archive.juryLibrary);
  const editions = (archive.editions || []).map((ed) => {
    if (!ed || !Array.isArray(ed.jury)) return ed;
    let edChanged = false;
    const jury = ed.jury.map((m) => {
      if (!m || m.personId) return m; // already a reference
      // Legacy inline member → find or create a library fiche.
      const nameKey = (m.name || "").trim().toLowerCase();
      let person = (m.id && byId[m.id]) || (nameKey && byName[nameKey]) || null;
      if (!person) {
        person = { id: m.id || personId(), name: m.name || "", photo: m.photo || "", bio: m.bio || "", role: m.role || "" };
        library.push(person);
        byId[person.id] = person;
        if (person.name) byName[person.name.trim().toLowerCase()] = person;
        changed = true;
      }
      edChanged = true;
      changed = true;
      return { personId: person.id, role: m.role || person.role || "", ...(m.hidden ? { hidden: true } : {}) };
    });
    return edChanged ? { ...ed, jury } : ed;
  });

  if (!changed) return { archive, changed: false };
  return { archive: { ...archive, juryLibrary: library, editions }, changed: true };
};
